import React, { FC } from 'react'
import { View, ViewStyle } from 'react-native'

import ErrorIcon from '@common/assets/svg/ErrorIcon'
import InfoIcon from '@common/assets/svg/InfoIcon'
import WarningIcon from '@common/assets/svg/WarningIcon'
import { Props as ButtonProps } from '@common/components/Button'
import DialogButton from '@common/components/Dialog/DialogButton'
import { Props as DualChoiceModalProps } from '@common/components/DualChoiceModal/DualChoiceModal'
import CommonText, { Props } from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import text from '@common/styles/utils/text'

import getStyles from './styles'

type Type = 'error' | 'warning' | 'info' | 'info3'

const DEFAULT_TYPE = 'warning'

const Wrapper: FC<{ children: React.ReactNode | React.ReactNode[] }> = ({ children }) => {
  const { styles } = useTheme(getStyles)

  return <View style={styles.container}>{children}</View>
}

const TitleAndIcon = ({
  title,
  style,
  type = DEFAULT_TYPE
}: {
  title: string
  type?: Type
  style?: ViewStyle
}) => {
  const { styles, theme } = useTheme(getStyles)
  const Icon =
    type === 'error' ? ErrorIcon : type === 'info3' || type === 'info' ? InfoIcon : WarningIcon

  return (
    <View style={[styles.titleAndIcon, style]}>
      {/* Icon in a rounded square tinted by the warning type (design-system
          modal convention — see DisconnectDappModal); title stays neutral. */}
      <View style={[styles.iconSquare, { backgroundColor: theme[`${type}Background`] }]}>
        <Icon width={28} height={28} color={theme[`${type}Decorative`]} />
      </View>
      <CommonText appearance="primaryText" weight="semiBold" fontSize={20} style={text.center}>
        {title}
      </CommonText>
    </View>
  )
}

const Text = ({ text, ...rest }: { text: string } & Props) => {
  return (
    <CommonText fontSize={16} appearance="secondaryText" {...rest}>
      {text}
    </CommonText>
  )
}

const ContentWrapper = ({ children, style }: { children: React.ReactNode; style?: ViewStyle }) => {
  const { styles } = useTheme(getStyles)

  return <View style={[styles.content, style]}>{children}</View>
}

const ButtonWrapper = ({
  children,
  reverse = false
}: {
  children: React.ReactNode
  reverse: boolean
}) => {
  const { styles } = useTheme(getStyles)

  return <View style={[styles.buttons, reverse && flexbox.directionRowReverse]}>{children}</View>
}

const DualChoiceWarningModal = ({
  title,
  description,
  onSecondaryButtonPress,
  onPrimaryButtonPress,
  primaryButtonText,
  children,
  secondaryButtonText,
  primaryButtonProps,
  secondaryButtonProps,
  type = DEFAULT_TYPE
}: Omit<DualChoiceModalProps, 'description' | 'primaryButtonTestID' | 'secondaryButtonTestID'> & {
  title: string
  description?: string
  children?: React.ReactNode | React.ReactNode[]
  primaryButtonProps?: Omit<ButtonProps, 'style'>
  secondaryButtonProps?: Omit<ButtonProps, 'style'>
  type?: Type
}) => {
  const { styles } = useTheme(getStyles)

  return (
    // Same shape as the design-system `Dialog` (see ForgotPasswordModal's "Reset
    // wallet?" confirm): left-aligned title + body, actions right-aligned with
    // the dismissive choice first. Keeps every confirm popup looking alike.
    <Wrapper>
      <ContentWrapper>
        <CommonText fontSize={18} weight="semiBold" style={spacings.mbMi}>
          {title}
        </CommonText>
        {!!description && <Text text={description} fontSize={14} style={spacings.mb} />}
        {children}
      </ContentWrapper>
      <View style={styles.buttons}>
        {secondaryButtonText && onSecondaryButtonPress && (
          <DialogButton
            text={secondaryButtonText}
            type="secondary"
            onPress={onSecondaryButtonPress}
            {...secondaryButtonProps}
          />
        )}
        <DialogButton
          text={primaryButtonText}
          onPress={onPrimaryButtonPress}
          type={type === 'error' ? 'danger' : 'primary'}
          {...primaryButtonProps}
        />
      </View>
    </Wrapper>
  )
}

DualChoiceWarningModal.Wrapper = Wrapper
DualChoiceWarningModal.TitleAndIcon = TitleAndIcon
DualChoiceWarningModal.Text = Text
DualChoiceWarningModal.ContentWrapper = ContentWrapper
DualChoiceWarningModal.ButtonWrapper = ButtonWrapper

export default DualChoiceWarningModal
